import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const RX = /^whsec_[A-Za-z0-9+/=]{16,}$/;
export const secretProblem = (s) => {
  const v = s.trim();
  if (!v) return null;
  if (!v.startsWith("whsec_")) return "A Resend signing secret starts with whsec_ — this looks like a different value (not stored).";
  if (!RX.test(v)) return "Unexpected characters or too short after whsec_ — copy the full signing secret from resend.com → Webhooks.";
  return null;
};

/** Signing-secret entry with in-page confirmation and an inline, persistent result (no native prompt/confirm, no toast-only feedback). */
export const WebhookSecretForm = ({ configured, hint, onSaved }) => {
  const [secret, setSecret] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const problem = secretProblem(secret);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/comms/sender/webhook-secret", { secret: secret.trim() });
      setResult({ ok: true, text: `Stored securely (ending ${data.hint}). Events will show here once the provider sends them.` });
      setSecret(""); onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      setResult({ ok: false, text: `Not stored: ${typeof d === "string" ? d : d?.message || e.message}` });
    }
    setBusy(false); setOpen(false);
  };
  return (
    <div className="space-y-1 pt-1">
      <div className="flex flex-wrap items-center gap-1">
        <Input type="password" autoComplete="off" value={secret} onChange={(e) => { setSecret(e.target.value); setResult(null); }} placeholder="whsec_… signing secret" aria-invalid={!!problem} className="h-7 w-72 text-[11px]" data-testid="comms-webhook-secret" />
        <Button size="sm" className="h-7 text-[11px]" disabled={!secret.trim() || !!problem || busy} onClick={() => setOpen(true)} data-testid="comms-webhook-save">Store securely</Button>
      </div>
      {problem && <div role="alert" className="text-[11px] text-rose-700" data-testid="comms-webhook-secret-invalid">{problem}</div>}
      {result && <div role="status" className={`rounded border px-2 py-1 text-[11px] ${result.ok ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-rose-300 bg-rose-50 text-rose-800"}`} data-testid="comms-webhook-save-result">{result.text}</div>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="comms-webhook-dialog">
          <DialogHeader>
            <DialogTitle>Store webhook signing secret</DialogTitle>
            <DialogDescription>The value is encrypted on the server and never shown again — only its last 4 characters.</DialogDescription>
          </DialogHeader>
          {configured && <div className="rounded border border-amber-300 bg-amber-50 p-2 text-[12px] text-amber-900" data-testid="comms-webhook-replace-warning">A secret is already stored{hint ? ` (${hint})` : ""}. Saving replaces it — use the secret of the endpoint registered at Resend, or signature checks will fail.</div>}
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="h-9 rounded-full border border-slate-300 px-4 text-[12px]" data-testid="comms-webhook-dialog-cancel">Cancel</button>
            <button onClick={save} disabled={busy} className="h-9 rounded-full bg-slate-900 px-4 text-[12px] font-semibold text-white disabled:opacity-40" data-testid="comms-webhook-dialog-confirm">{busy ? "Storing…" : "Store securely"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
